"use client";
import React, { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import "../AutoVerkaufen.css";

import BildTextInfo from "@/components/BildTextInfo/BildTextInfo";
import { TopSectionAutoVerkaufen } from "../sections/TopSection";
import { AutoVerkaufenGrid } from "@/common/AutoVerkaufenGrid";
import { Banner } from "@/common/Banner";
import sellCarCash from "@/assets/auto-verkaufen/Rückbank-autoverkauf.avif";
import höchstenPreis from "@/assets/auto-verkaufen/wir-kaufen-dein-auto-verkauf.avif";
import vonZuhauseAus from "@/assets/auto-verkaufen/Verkauft-handy.avif";
import { fragenVorarlberg } from "../data";
import { HäufigeFragen } from "@/common/HäufigeFragen";
import FAQImage from "@/assets/faq-autoverkauf-autoeinfachlos.webp";

// Only needed after step 11; keep auth-related code/CSS out of initial paint.
// Below-the-fold section; defer to reduce initial route JS/CSS.
const Ratgeber = dynamic(() => import("@/components/Ratgeber/Ratgeber"), {
  ssr: false,
});

const AnmeldenSection = dynamic(() => import("@/app/anmelden/AnmeldenStepRedirect"), {
  ssr: false,
});

function Vorarlberg() {
  const [step, setStep] = useState(1);
  // selected values
  const [selectedBrand, setSelectedBrand] = useState({ id: "", name: "" });
  const [selectedModel, setSelectedModel] = useState({ id: "", name: "" });
  const [selectedYear, setSelectedYear] = useState({ id: "", name: "" });
  const [selectedBauform, setSelectedBauform] = useState({ id: "", name: "" });
  const [selectedFuel, setSelectedFuel] = useState({ id: "", name: "" });
  const [selectedVariant, setSelectedVariant] = useState({ id: "", name: "" });
  const [selectedPs, setSelectedPs] = useState({ id: "", name: "" });
  const [selectedTransmission, setSelectedTransmission] = useState({
    id: "",
    name: "",
  });
  const [selectedDoors, setSelectedDoors] = useState({ id: "", name: "" });
  const [selectedModelType, setSelectedModelType] = useState({
    id: "",
    name: "",
  });
  const [selectedKmStand, setSelectedKmStand] = useState("");
  const isSecondaryMethod = true;

  useEffect(() => {
    if (step >= 10) {
      if (typeof AnmeldenSection?.preload === "function") {
        AnmeldenSection.preload();
      } else {
        import("@/app/anmelden/AnmeldenStepRedirect");
      }
    }
  }, [step]);


  return (
    <div className="home-container">
      <TopSectionAutoVerkaufen
        title={<>Auto in Vorarlberg verkaufen: Schnell, sicher & fair</>}
        text={
          <>
            84% der Autoeinfachlos-Kunden bekommen mehr Geld.*
            <br className="desktop" />
            Hol dir deine kostenlose und sofortige Bewertung für Vorarlberg.
          </>
        }
        buttonTitle="Bewertung ansehen"
        linkname="Vorarlberg"
        link="/auto-verkaufen/vorarlberg"
        customNav="Auto verkaufen"
        customNavLink="/auto-verkaufen"
        step={step}
        setStep={setStep}
        selectedBrand={selectedBrand}
        setSelectedBrand={setSelectedBrand}
        selectedModel={selectedModel}
        setSelectedModel={setSelectedModel}
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        selectedBauform={selectedBauform}
        setSelectedBauform={setSelectedBauform}
        selectedFuel={selectedFuel}
        setSelectedFuel={setSelectedFuel}
        selectedVariant={selectedVariant}
        setSelectedVariant={setSelectedVariant}
        selectedPs={selectedPs}
        setSelectedPs={setSelectedPs}
        selectedTransmission={selectedTransmission}
        setSelectedTransmission={setSelectedTransmission}
        selectedDoors={selectedDoors}
        setSelectedDoors={setSelectedDoors}
        setSelectedModelType={setSelectedModelType}
        selectedModelType={selectedModelType}
        selectedKmStand={selectedKmStand}
        setSelectedKmStand={setSelectedKmStand}
      />

      {step === 12 && (
        <>
          <AnmeldenSection
            title="Bitte gib deine E-Mail-Adresse ein, um fortzufahren"
            fin={null}
            isManual={isSecondaryMethod}
            marke={selectedBrand}
            model={selectedModel}
            baujahr={selectedYear}
            bodytype={selectedBauform}
            fueltype={selectedFuel}
            variant={selectedVariant}
            ps={selectedPs}
            gearbox={selectedTransmission}
            doors={selectedDoors}
            modelType={selectedModelType}
            kmStand={selectedKmStand}
            page="/auto-verkaufen/vorarlberg"
          />
          <Banner removeContent className="Banner-anmelden" />
        </>
      )}

      {step !== 12 && (
        <div className="Page background-color AV-below-fold">
          <div className="Page-wrapper">
            <AutoVerkaufenGrid
              image={sellCarCash}
              leftTitle="Du willst dein Auto in Vorarlberg verkaufen? Warum es oft komplizierter ist, als man denkt"
              leftText={
                <>
                  <p>
                    Ein Auto zu verkaufen ist nie eine alltägliche Entscheidung.
                    In Vorarlberg gibt es einige Besonderheiten, die den Verkauf
                    beeinflussen. Viele Menschen pendeln täglich in die Schweiz.
                    Familien brauchen verlässliche Kombis oder SUVs. Durch das
                    alpine Gelände sind Allradfahrzeuge besonders gefragt.
                    Gleichzeitig ist der Gebrauchtwagenmarkt stark umkämpft.
                    Dadurch wird es schwieriger, den richtigen Käufer zu finden.
                  </p>
                  <p>
                    Wenn du dein Auto in Vorarlberg verkaufen möchtest, brauchst
                    du eine Lösung, die schnell, transparent und sicher
                    funktioniert. Genau hier hilft dir Autoeinfachlos.
                  </p>
                </>
              }
              rightFirstTitle="Auto verkaufen in Vorarlberg – worauf kommt es an?"
              rightFirstText={
                <>
                  <p>
                    Ein Auto zu verkaufen klingt einfach. Inserat erstellen,
                    Interessenten treffen, Preis verhandeln. In der Praxis sieht
                    es aber anders aus.
                  </p>
                  <p>
                    <strong>Regionale Besonderheiten:</strong> In Vorarlberg sind
                    vor allem robuste und allradgetriebene Fahrzeuge beliebt. Sie
                    werden für das Montafon, Lech oder Pendlerfahrten über die
                    Grenze gebraucht. Ein kleiner Stadtwagen, der in Wien perfekt
                    passt, hat in Bregenz oder Feldkirch oft weniger Nachfrage.
                  </p>
                  <p>
                    <strong>Pendlerautos:</strong> Viele Vorarlberger fahren
                    täglich in die Schweiz. Dadurch haben Autos oft höhere
                    Kilometerstände. Käufer achten sehr auf Wartung und
                    Servicehistorie.
                  </p>
                  <p>
                    <strong>Familienfahrzeuge:</strong> Vans, Kombis und SUVs sind
                    sehr gefragt. Trotzdem kostet es privat viel Zeit und Nerven,
                    sie zu einem guten Preis zu verkaufen.
                  </p>
                  <p>
                    Privatverkauf bedeutet meistens Stress. Du musst Inserate
                    schalten, Termine vereinbaren und dich auf Preisverhandlungen
                    einstellen. Nicht selten bekommst du auch unseriöse Angebote.
                  </p>

                  <h3>Dein Vorteil mit Autoeinfachlos in Vorarlberg</h3>
                  <p>
                    Mit Autoeinfachlos verkaufst du dein Auto in ganz Vorarlberg.
                    Egal ob in Bregenz, Dornbirn, Feldkirch oder im
                    Bregenzerwald.
                  </p>
                  <p>
                    <strong>Deine Vorteile:</strong>
                  </p>
                  <ul className="custom-unordered-list">
                    <li>
                      Über 5.000 Händler bieten auf dein Auto, auch Händler aus
                      Vorarlberg
                    </li>
                    <li>
                      Du bekommst den besten Preis durch Auktion, nicht durch
                      harte Einzelverhandlungen
                    </li>
                    <li>Dein Auto wird direkt bei dir abgeholt</li>
                    <li>Sichere und schnelle Bezahlung</li>
                  </ul>
                  <p>So verkaufst du dein Auto einfach und ohne Risiko.</p>
                </>
              }
              rightSecondTitle="Autoankauf in Vorarlberg - privat oder Händler?"
              rightSecondText={
                <>
                  <p>
                    Die Frage stellen sich viele: Soll ich mein Auto privat
                    verkaufen oder an einen Händler in Vorarlberg?
                  </p>
                  <p>
                    <strong>Privatverkauf:</strong> Klingt oft nach mehr Geld. In
                    echt bedeutet das viele Anfragen, ständige Besichtigungen und
                    Unsicherheit bei der Zahlung.
                  </p>
                  <p>
                    <strong>Direkter Händlerverkauf:</strong> Ist zwar schnell,
                    aber ein einzelner Händler zahlt selten den Marktpreis. Er
                    will schließlich selbst Gewinn machen.
                  </p>
                  <p>
                    Die Lösung ist Autoeinfachlos. Du profitierst vom großen
                    Händlernetzwerk und bekommst durch die Auktion einen fairen
                    Marktpreis. Du musst selbst nicht verhandeln.
                  </p>
                </>
              }
            />

            <div className="Banner-sections">
              <BildTextInfo
                image={höchstenPreis}
                h2
                isBig
                fontSize="36px"
                title="Was ist dein Auto in Vorarlberg wert?"
                alt="Auto in Vorarlberg bewerten"
                text={
                  <>
                    <p>
                      Der Wert hängt von vielen Faktoren ab. Marke, Modell,
                      Baujahr, Laufleistung und Zustand. Auch die Nachfrage in
                      Vorarlberg spielt eine Rolle.
                    </p>
                    <p>
                      <strong>Beispiele:</strong>
                    </p>
                    <ul className="custom-unordered-list">
                      <li>Hat dein Auto Allrad?</li>
                      <li>
                        Wie hoch ist der Kilometerstand durch Pendeln in die
                        Schweiz?
                      </li>
                      <li>Ist es ein Familienauto oder ein Nischenmodell?</li>
                    </ul>
                    <p>
                      Bei Autoeinfachlos kannst du dein Auto kostenlos online
                      bewerten. So hast du sofort eine realistische Einschätzung.
                    </p>
                    <p>
                      <Link className="link" href="/auto-bewerten">
                        Jetzt Auto bewerten
                      </Link>
                    </p>

                    <h3>So funktioniert der Verkauf bei Autoeinfachlos in Vorarlberg</h3>
                    <p>Der Ablauf ist einfach.</p>
                    <ul className="custom-unordered-list">
                      <li>Gib dein Auto online zur Bewertung ein</li>
                      <li>Wir starten die Auktion im Händlernetzwerk</li>
                      <li>
                        Du erhältst das höchste Gebot und entscheidest, ob du
                        verkaufen willst
                      </li>
                      <li>Dein Auto wird direkt bei dir in Vorarlberg abgeholt</li>
                      <li>Du bekommst dein Geld sicher und schnell</li>
                    </ul>
                    <p>Schnell, fair und unkompliziert.</p>
                  </>
                }
              />
            </div>

            <HäufigeFragen
              title="Häufige Fragen zum Autoverkauf in Vorarlberg"
              fragen={fragenVorarlberg}
              image={FAQImage}
            />

            <div style={{ paddingTop: "120px" }}>
              <Ratgeber
                title="Mehr zum Thema Auto verkaufen in Vorarlberg "
                autoVerkaufen
                autoBewerten
                listenpreisBerechnen
                fahrgestellnummerPruefen
                kaputtesAuto
                wasistmeinautowert
              />
            </div>

            <div className="Banner-sections-marken">
              <BildTextInfo
                image={vonZuhauseAus}
                h2
                isBig
                fontSize="36px"
                title="Auto verkaufen in Vorarlberg mit Autoeinfachlos"
                alt="Auto verkaufen in Vorarlberg mit Autoeinfachlos"
                text={
                  <>
                    <p>
                      Privatverkauf kostet dich Zeit und Nerven. Mit
                      Autoeinfachlos verkaufst du dein Auto zum besten Preis.
                      Durch unser Händlernetzwerk, regionale Abholung und sichere
                      Bezahlung.
                    </p>
                    <p>Starte jetzt mit einer kostenlosen Bewertung:</p>
                    <p>
                      <Link className="link" href="/auto-bewerten">
                        Jetzt Auto kostenlos bewerten
                      </Link>
                    </p>
                  </>
                }
              />
            </div>
          </div>
          <Banner
            link="/auto-bewerten"
            title="Jetzt Auto kostenlos bewerten und verkaufen"
          />
        </div>
      )}
    </div>
  );
}

export default Vorarlberg;
